import { Navigate, createBrowserRouter } from "react-router-dom";
import { AppShellLayout, AuthLayout, LoadingLayout } from "@/layouts";
import { SignInPage } from "@/pages/SignInPage";
import { RouteErrorPage } from "@/pages/RouteErrorPage";
import {
  IdentityVerificationDetailPage,
  BirthdayChangeDetailPage,
  UserDetailPage,
  UsersDirectoryPage,
} from "@/modules/users";
import {
  BrandDealDetailPage,
  BrandDealsQueuePage,
  CampaignDetailPage,
  CampaignsQueuePage,
  CreatorHubPage,
  InvitationDetailPage,
  InvitationsQueuePage,
  MembershipDetailPage,
  MembershipsQueuePage,
  ParticipationDetailPage,
  ParticipationsQueuePage,
  PaymentDetailPage,
  PaymentsQueuePage,
  PerformanceDetailPage,
  PerformanceQueuePage,
  ProfileDetailPage,
  ProfilesQueuePage,
  ProgramDetailPage,
  ProgramsQueuePage,
  TierHistoryDetailPage,
  TierHistoryQueuePage,
} from "@/modules/creators";
import {
  BusinessDetailPage,
  BusinessesDirectoryPage,
  BusinessTypeDetailPage,
  BusinessTypesDirectoryPage,
  CancelledApplicationsPage,
  CredentialReviewPage,
  FreelancerSkillsPage,
} from "@/modules/businesses";
import {
  ResourceDetailPage,
  ResourcesDirectoryPage,
} from "@/modules/resources";
import {
  BookingDetailPage,
  BookingsDirectoryPage,
  HoldDetailPage,
  HoldsDirectoryPage,
} from "@/modules/bookings";
import {
  CommunitiesDirectoryPage,
  CommunityDetailPage,
} from "@/modules/communities";
import { EventDetailPage, EventsDirectoryPage } from "@/modules/events";
import {
  AppealDetailPage,
  AppealsQueuePage,
  CaseDetailPage,
  CasesQueuePage,
  EnforcementsQueuePage,
  ReportDetailPage,
  ReportsQueuePage,
  TrustSafetyHubPage,
} from "@/modules/trust-safety";
import {
  SupportTicketDetailPage,
  SupportTicketsDirectoryPage,
} from "@/modules/support";
import {
  AudienceDetailPage,
  AudiencesQueuePage,
  CampaignDetailPage as MarketingCampaignDetailPage,
  CampaignsQueuePage as MarketingCampaignsQueuePage,
  DeliveriesQueuePage,
  DeliveryDetailPage,
  MarketingHubPage,
  TemplateDetailPage,
  TemplatesQueuePage,
} from "@/modules/marketing";
import {
  MarketplaceHubPage,
  OpportunitiesQueuePage,
  OpportunityDetailPage,
  SignalDetailPage,
  SignalsQueuePage,
} from "@/modules/marketplace";
import {
  AnalyticsHubPage,
  SavedViewDetailPage,
  SavedViewsQueuePage,
  SegmentDetailPage,
  SegmentsQueuePage,
} from "@/modules/analytics";
import {
  ArticleDetailPage,
  ArticlesQueuePage,
  KnowledgeHubPage,
  PoliciesQueuePage,
  PolicyDetailPage,
} from "@/modules/knowledge";
import {
  TaskDetailPage,
  TasksHubPage,
  TasksQueuePage,
} from "@/modules/tasks";
import {
  AccessDetailPage,
  AccessQueuePage,
  AuditLogDetailPage,
  AuditLogsQueuePage,
  ConfigurationDetailPage,
  ConfigurationsQueuePage,
  FeatureFlagDetailPage,
  FeatureFlagsQueuePage,
  IntegrationEventDetailPage,
  IntegrationEventsQueuePage,
  MaintenanceDetailPage,
  MaintenanceQueuePage,
  SystemHubPage,
} from "@/modules/system";
import { DashboardPage } from "@/modules/dashboard";
import { StaffCreatePage, StaffDetailPage, StaffListPage } from "@/modules/staff";
import {
  ApprovalDetailPage,
  ApprovalsHubPage,
  ApprovalsQueuePage,
} from "@/modules/approvals";
import {
  CommissionRuleDetailPage,
  CommissionRulesQueuePage,
  DisputeDetailPage,
  DisputesQueuePage,
  ExceptionDetailPage,
  ExceptionsQueuePage,
  FinanceHubPage,
  PayoutDetailPage,
  PayoutsQueuePage,
  RefundDetailPage,
  RefundsQueuePage,
  SettlementDetailPage,
  SettlementsQueuePage,
  TransactionDetailPage,
  TransactionsQueuePage,
} from "@/modules/finance";

/**
 * Phase K.17 routing — prior modules + Approvals.
 */
export const router = createBrowserRouter([
  {
    path: "/",
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to="/app" replace /> },
      {
        path: "auth",
        element: <AuthLayout />,
        children: [
          { index: true, element: <Navigate to="sign-in" replace /> },
          { path: "sign-in", element: <SignInPage /> },
        ],
      },
      {
        path: "app",
        element: <AppShellLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "dashboard", element: <DashboardPage /> },
          { path: "staff", element: <StaffListPage /> },
          { path: "staff/new", element: <StaffCreatePage /> },
          { path: "staff/:userId", element: <StaffDetailPage /> },
          { path: "users", element: <UsersDirectoryPage /> },
          { path: "users/verifications", element: <Navigate to="/app/creators?tab=applications" replace /> },
          {
            path: "users/verifications/:verificationId",
            element: <IdentityVerificationDetailPage />,
          },
          { path: "users/:userId", element: <UserDetailPage /> },
          { path: "creators", element: <CreatorHubPage /> },
          { path: "creators/profiles", element: <ProfilesQueuePage /> },
          {
            path: "creators/profiles/:profileId",
            element: <ProfileDetailPage />,
          },
          {
            path: "creators/applications",
            element: <Navigate to="/app/creators?tab=applications" replace />,
          },
          {
            path: "creators/applications/:applicationId",
            element: <IdentityVerificationDetailPage />,
          },
          { path: "creators/birthday-changes/:requestId", element: <BirthdayChangeDetailPage /> },
          {
            path: "creators/tier-history",
            element: <TierHistoryQueuePage />,
          },
          {
            path: "creators/tier-history/:tierHistoryId",
            element: <TierHistoryDetailPage />,
          },
          { path: "creators/programs", element: <ProgramsQueuePage /> },
          {
            path: "creators/programs/:programId",
            element: <ProgramDetailPage />,
          },
          {
            path: "creators/memberships",
            element: <MembershipsQueuePage />,
          },
          {
            path: "creators/memberships/:membershipId",
            element: <MembershipDetailPage />,
          },
          { path: "creators/campaigns", element: <CampaignsQueuePage /> },
          {
            path: "creators/campaigns/:campaignId",
            element: <CampaignDetailPage />,
          },
          {
            path: "creators/invitations",
            element: <InvitationsQueuePage />,
          },
          {
            path: "creators/invitations/:invitationId",
            element: <InvitationDetailPage />,
          },
          {
            path: "creators/participations",
            element: <ParticipationsQueuePage />,
          },
          {
            path: "creators/participations/:participationId",
            element: <ParticipationDetailPage />,
          },
          {
            path: "creators/brand-deals",
            element: <BrandDealsQueuePage />,
          },
          {
            path: "creators/brand-deals/:brandDealId",
            element: <BrandDealDetailPage />,
          },
          {
            path: "creators/performance",
            element: <PerformanceQueuePage />,
          },
          {
            path: "creators/performance/:recordId",
            element: <PerformanceDetailPage />,
          },
          { path: "creators/payments", element: <PaymentsQueuePage /> },
          {
            path: "creators/payments/:paymentId",
            element: <PaymentDetailPage />,
          },
          { path: "businesses/types", element: <BusinessTypesDirectoryPage /> },
          { path: "businesses/skills", element: <FreelancerSkillsPage /> },
          {
            path: "businesses/types/:businessTypeId",
            element: <BusinessTypeDetailPage />,
          },
          { path: "businesses/resources", element: <ResourcesDirectoryPage /> },
          {
            path: "businesses/resources/:resourceId",
            element: <ResourceDetailPage />,
          },
          {
            path: "businesses/cancelled",
            element: <CancelledApplicationsPage />,
          },
          {
            path: "businesses/credentials",
            element: <CredentialReviewPage />,
          },
          { path: "businesses", element: <BusinessesDirectoryPage /> },
          { path: "businesses/:businessId", element: <BusinessDetailPage /> },
          { path: "bookings/holds", element: <HoldsDirectoryPage /> },
          // Events (i88-i90), read only.
          { path: "bookings/events", element: <EventsDirectoryPage /> },
          { path: "bookings/events/:eventId", element: <EventDetailPage /> },
          {
            path: "bookings/holds/:holdId",
            element: <HoldDetailPage />,
          },
          { path: "communities", element: <CommunitiesDirectoryPage /> },
          {
            path: "communities/:communityId",
            element: <CommunityDetailPage />,
          },
          { path: "trust-safety", element: <TrustSafetyHubPage /> },
          {
            path: "trust-safety/reports",
            element: <ReportsQueuePage />,
          },
          {
            path: "trust-safety/reports/:reportId",
            element: <ReportDetailPage />,
          },
          { path: "trust-safety/cases", element: <CasesQueuePage /> },
          {
            path: "trust-safety/cases/:caseId",
            element: <CaseDetailPage />,
          },
          {
            path: "trust-safety/enforcements",
            element: <EnforcementsQueuePage />,
          },
          {
            path: "trust-safety/appeals",
            element: <AppealsQueuePage />,
          },
          {
            path: "trust-safety/appeals/:appealId",
            element: <AppealDetailPage />,
          },
          { path: "support", element: <SupportTicketsDirectoryPage /> },
          {
            path: "support/tickets/:ticketId",
            element: <SupportTicketDetailPage />,
          },
          { path: "marketing", element: <MarketingHubPage /> },
          {
            path: "marketing/campaigns",
            element: <MarketingCampaignsQueuePage />,
          },
          {
            path: "marketing/campaigns/:campaignId",
            element: <MarketingCampaignDetailPage />,
          },
          {
            path: "marketing/templates",
            element: <TemplatesQueuePage />,
          },
          {
            path: "marketing/templates/:templateId",
            element: <TemplateDetailPage />,
          },
          {
            path: "marketing/audiences",
            element: <AudiencesQueuePage />,
          },
          {
            path: "marketing/audiences/:audienceId",
            element: <AudienceDetailPage />,
          },
          {
            path: "marketing/deliveries",
            element: <DeliveriesQueuePage />,
          },
          {
            path: "marketing/deliveries/:deliveryId",
            element: <DeliveryDetailPage />,
          },
          { path: "marketplace", element: <MarketplaceHubPage /> },
          {
            path: "marketplace/signals",
            element: <SignalsQueuePage />,
          },
          {
            path: "marketplace/signals/:signalId",
            element: <SignalDetailPage />,
          },
          {
            path: "marketplace/opportunities",
            element: <OpportunitiesQueuePage />,
          },
          {
            path: "marketplace/opportunities/:opportunityId",
            element: <OpportunityDetailPage />,
          },
          { path: "analytics", element: <AnalyticsHubPage /> },
          {
            path: "analytics/saved-views",
            element: <SavedViewsQueuePage />,
          },
          {
            path: "analytics/saved-views/:viewId",
            element: <SavedViewDetailPage />,
          },
          {
            path: "analytics/segments",
            element: <SegmentsQueuePage />,
          },
          {
            path: "analytics/segments/:segmentId",
            element: <SegmentDetailPage />,
          },
          { path: "knowledge", element: <KnowledgeHubPage /> },
          {
            path: "knowledge/articles",
            element: <ArticlesQueuePage />,
          },
          {
            path: "knowledge/articles/:articleId",
            element: <ArticleDetailPage />,
          },
          {
            path: "knowledge/policies",
            element: <PoliciesQueuePage />,
          },
          {
            path: "knowledge/policies/:policyId",
            element: <PolicyDetailPage />,
          },
          { path: "tasks", element: <TasksHubPage /> },
          { path: "tasks/queue", element: <TasksQueuePage /> },
          { path: "tasks/:taskId", element: <TaskDetailPage /> },
          { path: "approvals", element: <ApprovalsHubPage /> },
          { path: "approvals/queue", element: <ApprovalsQueuePage /> },
          {
            path: "approvals/:approvalId",
            element: <ApprovalDetailPage />,
          },
          { path: "system", element: <SystemHubPage /> },
          {
            path: "system/maintenance",
            element: <MaintenanceQueuePage />,
          },
          {
            path: "system/maintenance/:maintenanceId",
            element: <MaintenanceDetailPage />,
          },
          {
            path: "system/configurations",
            element: <ConfigurationsQueuePage />,
          },
          {
            path: "system/configurations/:configurationId",
            element: <ConfigurationDetailPage />,
          },
          {
            path: "system/feature-flags",
            element: <FeatureFlagsQueuePage />,
          },
          {
            path: "system/feature-flags/:flagId",
            element: <FeatureFlagDetailPage />,
          },
          { path: "system/access", element: <AccessQueuePage /> },
          {
            path: "system/access/:assignmentId",
            element: <AccessDetailPage />,
          },
          {
            path: "system/audit-logs",
            element: <AuditLogsQueuePage />,
          },
          {
            path: "system/audit-logs/:auditLogId",
            element: <AuditLogDetailPage />,
          },
          {
            path: "system/integration-events",
            element: <IntegrationEventsQueuePage />,
          },
          {
            path: "system/integration-events/:eventId",
            element: <IntegrationEventDetailPage />,
          },
          { path: "bookings", element: <BookingsDirectoryPage /> },
          { path: "bookings/:bookingId", element: <BookingDetailPage /> },
          { path: "finance", element: <FinanceHubPage /> },
          {
            path: "finance/transactions",
            element: <TransactionsQueuePage />,
          },
          {
            path: "finance/transactions/:transactionId",
            element: <TransactionDetailPage />,
          },
          { path: "finance/refunds", element: <RefundsQueuePage /> },
          {
            path: "finance/refunds/:refundId",
            element: <RefundDetailPage />,
          },
          { path: "finance/payouts", element: <PayoutsQueuePage /> },
          {
            path: "finance/payouts/:payoutId",
            element: <PayoutDetailPage />,
          },
          {
            path: "finance/settlements",
            element: <SettlementsQueuePage />,
          },
          {
            path: "finance/settlements/:settlementId",
            element: <SettlementDetailPage />,
          },
          {
            path: "finance/commission-rules",
            element: <CommissionRulesQueuePage />,
          },
          {
            path: "finance/commission-rules/:ruleId",
            element: <CommissionRuleDetailPage />,
          },
          { path: "finance/exceptions", element: <ExceptionsQueuePage /> },
          {
            path: "finance/exceptions/:exceptionId",
            element: <ExceptionDetailPage />,
          },
          { path: "finance/disputes", element: <DisputesQueuePage /> },
          {
            path: "finance/disputes/:disputeId",
            element: <DisputeDetailPage />,
          },
        ],
      },
      {
        path: "loading",
        element: <LoadingLayout />,
      },
      {
        path: "*",
        element: <RouteErrorPage />,
      },
    ],
  },
]);
